// VisitorDashboardPage.js
// Visitor Management – Live Dashboard (role & scope aware)

import React, { useEffect, useState, useContext, useMemo } from "react";
import axiosClient from "../../../api/axiosClient";
import { AuthContext } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";

// NEW: Visitor AI fullscreen assistant import
import VisitorAIFullscreenAssistant from "../../../visitor_ai/components/VisitorAIFullscreenAssistant";

function VisitorDashboardPage() {
  const { activeScope } = useContext(AuthContext);
  const navigate = useNavigate();

  const tenant_id = activeScope?.tenant_id;
  const location_id = activeScope?.location_id;
  const role_code = activeScope?.role_code; // still available if needed later

  const [cards, setCards] = useState({
    open_requests: 0,
    approved: 0,
    checked_in: 0,
    checked_out: 0,
    rejected: 0,
    total_today: 0,
  });

  const [rows, setRows] = useState([]);
  const [hostOptions, setHostOptions] = useState([]);

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [hostFilter, setHostFilter] = useState("ALL");
  const [searchText, setSearchText] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // NEW: open state for Visitor AI modal
  const [openAI, setOpenAI] = useState(false);

  const totalPages = useMemo(
    () => (total > 0 ? Math.ceil(total / pageSize) : 1),
    [total, pageSize]
  );

  // -----------------------------
  // Load dashboard data
  // -----------------------------
  useEffect(() => {
    if (!tenant_id || !location_id) {
      setError("Tenant / Location missing in client scope.");
      return;
    }

    const loadDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = {
          tenant_id,
          location_id,
          page,
          page_size: pageSize,
        };

        if (statusFilter && statusFilter !== "ALL") {
          params.status = statusFilter;
        }
        if (hostFilter && hostFilter !== "ALL") {
          params.host = hostFilter;
        }
        if (searchText) {
          params.search = searchText;
        }

        const res = await axiosClient.get(
          "/visitor-management/dashboard/summary",
          { params }
        );

        const summary = res.data?.data?.summary || {};

        setCards(summary.cards || {});
        setRows(summary.list?.rows || []);
        setTotal(summary.list?.total || 0);
        setHostOptions(summary.filters?.host_options || []);
      } catch (err) {
        console.error("Dashboard load error:", err);
        setError(
          err?.response?.data?.message || "Failed to load visitor dashboard."
        );
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [
    tenant_id,
    location_id,
    page,
    pageSize,
    statusFilter,
    hostFilter,
    searchText,
  ]);

  // -----------------------------
  // Helpers
  // -----------------------------
  const statusBadge = (status) => {
    const s = (status || "").toUpperCase();
    switch (s) {
      case "PENDING":
        return <span className="badge bg-warning text-dark">Pending</span>;
      case "APPROVED":
        return <span className="badge bg-success">Approved</span>;
      case "REJECTED":
        return <span className="badge bg-danger">Rejected</span>;
      case "CHECKED_IN":
        return <span className="badge bg-info text-dark">Checked-in</span>;
      case "CHECKED_OUT":
        return <span className="badge bg-secondary">Checked-out</span>;
      default:
        return <span className="badge bg-light text-dark">{status}</span>;
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "--";
    // iso could be "HH:MM:SS" or full ISO date-time
    const t = iso.includes("T") ? iso.split("T")[1] : iso;
    return t.slice(0, 5);
  };

  const handleClearFilters = () => {
    setStatusFilter("ALL");
    setHostFilter("ALL");
    setSearchText("");
    setPage(1);
  };

  const handlePrevPage = () => {
    setPage((p) => (p > 1 ? p - 1 : p));
  };

  const handleNextPage = () => {
    setPage((p) => (p < totalPages ? p + 1 : p));
  };

  // -----------------------------
  // Render
  // -----------------------------
  if (!tenant_id || !location_id) {
    return (
      <div className="container py-5 text-danger">
        Tenant / Location missing in client scope.
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div
        className="mb-4 px-4 py-3 text-white d-flex justify-content-between align-items-center"
        style={{
          borderRadius: "18px",
          background:
            "linear-gradient(90deg, #3f51b5 0%, #5c6bc0 40%, #2196f3 100%)",
        }}
      >
        <div>
          <h3 className="mb-0 fw-bold">Visitor Dashboard</h3>
          <small className="text-light">
            Real-time visitor activity and host overview.
          </small>
        </div>

        {/* Right side: Create Visit button (no scope/role display) */}
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-light btn-sm fw-semibold"
            onClick={() => navigate("/visitor/create-visit")}
          >
            + Create Visit
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="mb-3">
        <input
          className="form-control form-control-lg"
          placeholder="Search by visitor / mobile / host / company / status..."
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(1);
          }}
          style={{ borderRadius: "18px" }}
        />
      </div>

      {/* Summary cards */}
      <div className="row g-3 mb-3">
        <div className="col-md-2 col-sm-6">
          <div
            className="p-3 text-white"
            style={{
              borderRadius: "18px",
              background:
                "linear-gradient(135deg, #7b1fa2 0%, #9c27b0 60%, #ba68c8 100%)",
            }}
          >
            <div className="small">Open Requests</div>
            <div className="h3 mb-0 fw-bold">{cards.open_requests || 0}</div>
          </div>
        </div>
        <div className="col-md-2 col-sm-6">
          <div
            className="p-3 text-white"
            style={{
              borderRadius: "18px",
              background:
                "linear-gradient(135deg, #1b5e20 0%, #2e7d32 60%, #66bb6a 100%)",
            }}
          >
            <div className="small">Approved</div>
            <div className="h3 mb-0 fw-bold">{cards.approved || 0}</div>
          </div>
        </div>
        <div className="col-md-2 col-sm-6">
          <div
            className="p-3 text-white"
            style={{
              borderRadius: "18px",
              background:
                "linear-gradient(135deg, #1565c0 0%, #1976d2 60%, #64b5f6 100%)",
            }}
          >
            <div className="small">Checked-In</div>
            <div className="h3 mb-0 fw-bold">{cards.checked_in || 0}</div>
          </div>
        </div>
        <div className="col-md-2 col-sm-6">
          <div
            className="p-3 text-white"
            style={{
              borderRadius: "18px",
              background:
                "linear-gradient(135deg, #4527a0 0%, #5e35b1 60%, #9575cd 100%)",
            }}
          >
            <div className="small">Checked-Out</div>
            <div className="h3 mb-0 fw-bold">{cards.checked_out || 0}</div>
          </div>
        </div>
        <div className="col-md-2 col-sm-6">
          <div
            className="p-3 text-white"
            style={{
              borderRadius: "18px",
              background:
                "linear-gradient(135deg, #b71c1c 0%, #d32f2f 60%, #e57373 100%)",
            }}
          >
            <div className="small">Rejected</div>
            <div className="h3 mb-0 fw-bold">{cards.rejected || 0}</div>
          </div>
        </div>
        <div className="col-md-2 col-sm-6">
          <div
            className="p-3 text-white"
            style={{
              borderRadius: "18px",
              background:
                "linear-gradient(135deg, #ff8f00 0%, #ffa000 60%, #ffca28 100%)",
            }}
          >
            <div className="small">Total Today</div>
            <div className="h3 mb-0 fw-bold">{cards.total_today || 0}</div>
          </div>
        </div>
      </div>

      {/* Filters row */}
      <div className="card shadow-sm mb-3" style={{ borderRadius: "18px" }}>
        <div className="card-body d-flex flex-wrap gap-3 align-items-end">
          <div style={{ minWidth: "180px" }}>
            <label className="form-label small mb-1">Status</label>
            <select
              className="form-select form-select-sm"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="ALL">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="CHECKED_IN">Checked-In</option>
              <option value="CHECKED_OUT">Checked-Out</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>

          <div style={{ minWidth: "180px" }}>
            <label className="form-label small mb-1">Host</label>
            <select
              className="form-select form-select-sm"
              value={hostFilter}
              onChange={(e) => {
                setHostFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="ALL">All Hosts</option>
              {hostOptions.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </div>

          <div className="ms-auto d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleClearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error / Loading */}
      {error && (
        <div className="alert alert-danger py-2 small" role="alert">
          {error}
        </div>
      )}
      {loading && (
        <div className="alert alert-info py-2 small" role="alert">
          Loading dashboard...
        </div>
      )}

      {/* Table */}
      <div className="card shadow-sm" style={{ borderRadius: "18px" }}>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "40px" }}>#</th>
                  <th>Visitor</th>
                  <th>Company</th>
                  <th>Host</th>
                  <th>Status</th>
                  <th style={{ width: "70px" }}>In</th>
                  <th style={{ width: "70px" }}>Out</th>
                  <th style={{ width: "80px" }}>View</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, index) => (
                  <tr key={r.id}>
                    <td>{(page - 1) * pageSize + index + 1}</td>
                    <td>{r.visitor_name}</td>
                    <td>{r.company}</td>
                    <td>{r.host_name}</td>
                    <td>{statusBadge(r.status)}</td>
                    <td>{formatTime(r.checkin_time)}</td>
                    <td>{formatTime(r.checkout_time)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => navigate(`/visitor/approvals/${r.visit_id}?source=dashboard`)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-3 text-muted">
                      No visits found for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-flex justify-content-between align-items-center px-3 py-2 small text-muted">
            <span>
              Page {page} of {totalPages} • {total} records
            </span>
            <div className="btn-group btn-group-sm">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handlePrevPage}
                disabled={page <= 1}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleNextPage}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Visitor AI launcher */}
      <button
        style={{
          position: "fixed",
          right: 24,
          bottom: 24,
          zIndex: 13000,
          background: "linear-gradient(90deg,#6a00f4,#ff4db6)",
          color: "#fff",
          borderRadius: 28,
          padding: "12px 18px",
          border: "none",
          boxShadow: "0 8px 24px rgba(106,0,244,0.24)"
        }}
        onClick={() => setOpenAI(true)}
      >
        Visitor AI
      </button>

      {/* Visitor AI Fullscreen Modal */}
      {openAI && (
        <VisitorAIFullscreenAssistant
          pageContext={{ tenant_id, location_id, user_id: activeScope?.user_id || activeScope?.id, role_code }}
          onClose={() => setOpenAI(false)}
        />
      )}
    </div>
  );
}

export default VisitorDashboardPage;
